import React, { useEffect, useMemo, useState } from "react";
import {
    createAdminAssetCarModel,
    createAdminAssetDriverMedia,
    deleteAdminAssetBrand,
    deleteAdminAssetCarModel,
    deleteAdminAssetClass,
    deleteAdminAssetDriverMedia,
    deleteAdminAssetFlag,
    fetchAdminAssetBrands,
    fetchAdminAssetCarModels,
    fetchAdminAssetClasses,
    fetchAdminAssetDriverMedia,
    fetchAdminAssetFlags,
    fetchManagerSeriesSeasons,
    fetchManagerTeams,
    runAdminAssetsBackfill,
    upsertAdminAssetBrand,
    upsertAdminAssetClass,
    upsertAdminAssetFlag,
    updateAdminAssetCarModel,
    updateAdminAssetDriverMedia,
    updateManagerTeam,
} from "../../../lib/managerApi.js";

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || "Fehler";
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    return String(err);
}

function ImgPreview({ src, alt = "preview", w = 90, h = 48 }) {
    if (!src) return <span className="muted">keine URL</span>;
    return <img src={src} alt={alt} style={{ width: w, height: h, objectFit: "contain", borderRadius: 8, background: "rgba(255,255,255,.04)" }} />;
}

function sectionCardStyle() {
    return {
        border: "1px solid rgba(255,255,255,.12)",
        borderRadius: 14,
        padding: 12,
        background: "rgba(255,255,255,.03)",
        display: "grid",
        gap: 10,
    };
}

function formGridStyle(minColPx = 180) {
    return {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColPx}px, 1fr))`,
        gap: 8,
        alignItems: "center",
    };
}

function firstSeasonEntry(team, seasonId) {
    const entries = team?.season_entries || [];
    return entries.find((e) => String(e.season_id) === String(seasonId)) || entries[0] || null;
}

export default function AssetManagement() {
    const [seriesRows, setSeriesRows] = useState([]);
    const [seriesKey, setSeriesKey] = useState("gt3x");
    const [seasonId, setSeasonId] = useState("");

    const [flags, setFlags] = useState([]);
    const [brands, setBrands] = useState([]);
    const [cars, setCars] = useState([]);
    const [media, setMedia] = useState([]);
    const [classes, setClasses] = useState([]);
    const [teams, setTeams] = useState([]);

    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [flagForm, setFlagForm] = useState({ country_code: "", display_name: "", flag_url: "" });
    const [brandForm, setBrandForm] = useState({ brand_key: "", display_name: "", logo_url: "", website_url: "", status: "active", sort_order: "0" });
    const [carForm, setCarForm] = useState({ brand_id: "", iracing_car_id: "", iracing_car_name: "", display_name: "", image_url: "", status: "active" });
    const [carEditId, setCarEditId] = useState(null);
    const [mediaForm, setMediaForm] = useState({ driver_id: "", season_id: "", iracing_cust_id: "", driver_name_key: "", country_code: "", flag_url: "", driver_image_url: "", profile_url: "" });
    const [mediaEditId, setMediaEditId] = useState(null);
    const [classForm, setClassForm] = useState({ class_key: "", display_name: "", image_url: "", sort_order: "0" });
    const [teamLogoDraft, setTeamLogoDraft] = useState({});

    const selectedSeries = useMemo(() => seriesRows.find((row) => row.series_key === seriesKey) || null, [seriesRows, seriesKey]);
    const seasons = useMemo(() => selectedSeries?.seasons || [], [selectedSeries]);

    async function loadAll() {
        setLoading(true);
        setError("");
        try {
            const [flagRes, brandRes, carRes, mediaRes, classRes] = await Promise.all([
                fetchAdminAssetFlags(),
                fetchAdminAssetBrands(),
                fetchAdminAssetCarModels(),
                fetchAdminAssetDriverMedia(),
                fetchAdminAssetClasses(),
            ]);

            setFlags(flagRes?.items || []);
            setBrands(brandRes?.items || []);
            setCars(carRes?.items || []);
            setMedia(mediaRes?.items || []);
            setClasses(classRes?.items || []);

            if (seasonId) {
                const teamRes = await fetchManagerTeams({ series_key: seriesKey, season_id: seasonId, team_status: "all" });
                setTeams(teamRes?.teams || []);
            } else {
                setTeams([]);
            }
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const data = await fetchManagerSeriesSeasons();
                if (!active) return;
                const rows = Array.isArray(data?.series) ? data.series : [];
                setSeriesRows(rows);
                const pref = rows.find((s) => s.series_key === "gt3x") || rows[0] || null;
                if (pref) {
                    setSeriesKey(pref.series_key);
                    const activeSeason = (pref.seasons || []).find((s) => s.status === "active") || pref.seasons?.[0];
                    if (activeSeason) setSeasonId(String(activeSeason.id));
                }
            } catch (err) {
                if (active) setError(prettyError(err));
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (!seriesKey) return;
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seriesKey, seasonId]);

    async function wrapAction(fn, okMsg) {
        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await fn();
            setSuccess(okMsg);
            await loadAll();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Liga Manager</div>
                        <h1 className="rv-page-title">Asset Management</h1>
                        <p className="rv-page-subtitle">Flags, Teams, Brands, Car Models, Driver Media und Class Assets zentral pflegen.</p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontWeight: 800, fontSize: 13 }}>Serie</span>
                        <select className="input" value={seriesKey} onChange={(e) => setSeriesKey(e.target.value)}>
                            {seriesRows.map((row) => <option key={row.id} value={row.series_key}>{row.display_name}</option>)}
                        </select>
                    </label>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button className="btn" onClick={loadAll} disabled={loading || busy}>{loading ? "Lade..." : "Neu laden"}</button>
                </div>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>Team Logos (Saison)</h2>
                    <div style={sectionCardStyle()}>
                        <div style={formGridStyle(220)}>
                            <select className="input" value={seasonId} onChange={(e) => setSeasonId(e.target.value)}>
                                {seasons.map((row) => <option key={row.id} value={row.id}>{row.display_name} ({row.status})</option>)}
                            </select>
                            <button
                                className="btn btn--accent"
                                disabled={busy || !seasonId}
                                onClick={() => wrapAction(() => runAdminAssetsBackfill({ season_id: Number(seasonId) }), "Asset-Backfill ausgefuehrt.")}
                            >
                                Asset-Backfill (Saison) starten
                            </button>
                        </div>
                    </div>
                    <div style={{ display: "grid", gap: 8 }}>
                        {teams.map((team) => {
                            const entry = firstSeasonEntry(team, seasonId);
                            if (!entry) return null;
                            const draft = teamLogoDraft[entry.id] ?? (entry.logo_url || "");
                            return (
                                <div key={entry.id} style={sectionCardStyle()}>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                        <strong>{entry.display_name}</strong>
                                        <ImgPreview src={draft} alt={entry.display_name} />
                                    </div>
                                    <input className="input" value={draft} onChange={(e) => setTeamLogoDraft((p) => ({ ...p, [entry.id]: e.target.value }))} placeholder="https://..." />
                                    <div>
                                        <button
                                            className="btn btn--accent"
                                            disabled={busy}
                                            onClick={() => wrapAction(
                                                () => updateManagerTeam(entry.id, { logo_url: draft, display_name: entry.display_name, status: entry.status || "active", sort_order: entry.sort_order || 0 }),
                                                `Teamlogo gespeichert: ${entry.display_name}`,
                                            )}
                                        >
                                            Teamlogo speichern
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>Country Flags</h2>
                    <div style={sectionCardStyle()}>
                        <div style={formGridStyle(180)}>
                            <input className="input" placeholder="DE" value={flagForm.country_code} onChange={(e) => setFlagForm((p) => ({ ...p, country_code: e.target.value }))} />
                            <input className="input" placeholder="Display Name" value={flagForm.display_name} onChange={(e) => setFlagForm((p) => ({ ...p, display_name: e.target.value }))} />
                            <input className="input" placeholder="Flag URL" value={flagForm.flag_url} onChange={(e) => setFlagForm((p) => ({ ...p, flag_url: e.target.value }))} />
                            <button className="btn btn--accent" disabled={busy} onClick={() => wrapAction(() => upsertAdminAssetFlag(flagForm), "Flag gespeichert.")}>Speichern</button>
                        </div>
                    </div>
                    {flags.map((row) => (
                        <div key={row.country_code} style={sectionCardStyle()}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                                <strong>{row.country_code} {row.display_name ? `- ${row.display_name}` : ""}</strong>
                                <ImgPreview src={row.flag_url} alt={row.country_code} w={70} h={40} />
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button className="btn" onClick={() => setFlagForm({ country_code: row.country_code, display_name: row.display_name || "", flag_url: row.flag_url || "" })}>In Formular laden</button>
                                <button className="btn" disabled={busy} onClick={() => wrapAction(() => deleteAdminAssetFlag(row.country_code), `Flag geloescht: ${row.country_code}`)}>Loeschen</button>
                            </div>
                        </div>
                    ))}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>Brands</h2>
                    <div style={sectionCardStyle()}>
                        <div style={formGridStyle(180)}>
                            <input className="input" placeholder="brand_key" value={brandForm.brand_key} onChange={(e) => setBrandForm((p) => ({ ...p, brand_key: e.target.value }))} />
                            <input className="input" placeholder="Display Name" value={brandForm.display_name} onChange={(e) => setBrandForm((p) => ({ ...p, display_name: e.target.value }))} />
                            <input className="input" placeholder="Logo URL" value={brandForm.logo_url} onChange={(e) => setBrandForm((p) => ({ ...p, logo_url: e.target.value }))} />
                            <input className="input" type="number" min="0" value={brandForm.sort_order} onChange={(e) => setBrandForm((p) => ({ ...p, sort_order: e.target.value }))} />
                            <button className="btn btn--accent" disabled={busy} onClick={() => wrapAction(() => upsertAdminAssetBrand({ ...brandForm, sort_order: Number(brandForm.sort_order || 0) }), "Brand gespeichert.")}>Speichern</button>
                        </div>
                    </div>
                    {brands.map((row) => (
                        <div key={row.id} style={sectionCardStyle()}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                                <strong>{row.display_name} ({row.brand_key})</strong>
                                <ImgPreview src={row.logo_url} alt={row.display_name} w={90} h={40} />
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button className="btn" onClick={() => setBrandForm({ brand_key: row.brand_key || "", display_name: row.display_name || "", logo_url: row.logo_url || "", website_url: row.website_url || "", status: row.status || "active", sort_order: String(row.sort_order || 0) })}>In Formular laden</button>
                                <button className="btn" disabled={busy} onClick={() => wrapAction(() => deleteAdminAssetBrand(row.id), `Brand geloescht: ${row.display_name}`)}>Loeschen</button>
                            </div>
                        </div>
                    ))}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>Car Models</h2>
                    <div style={sectionCardStyle()}>
                        <div style={formGridStyle(180)}>
                            <select className="input" value={carForm.brand_id} onChange={(e) => setCarForm((p) => ({ ...p, brand_id: e.target.value }))}>
                                <option value="">Brand</option>
                                {brands.map((b) => <option key={b.id} value={b.id}>{b.display_name}</option>)}
                            </select>
                            <input className="input" placeholder="iRacing Car ID" value={carForm.iracing_car_id} onChange={(e) => setCarForm((p) => ({ ...p, iracing_car_id: e.target.value }))} />
                            <input className="input" placeholder="iRacing Car Name" value={carForm.iracing_car_name} onChange={(e) => setCarForm((p) => ({ ...p, iracing_car_name: e.target.value }))} />
                            <input className="input" placeholder="Display Name" value={carForm.display_name} onChange={(e) => setCarForm((p) => ({ ...p, display_name: e.target.value }))} />
                            <input className="input" placeholder="Image URL" value={carForm.image_url} onChange={(e) => setCarForm((p) => ({ ...p, image_url: e.target.value }))} />
                            <button
                                className="btn btn--accent"
                                disabled={busy}
                                onClick={() => wrapAction(
                                    () => (carEditId ? updateAdminAssetCarModel(carEditId, { ...carForm, brand_id: carForm.brand_id ? Number(carForm.brand_id) : null, iracing_car_id: carForm.iracing_car_id ? Number(carForm.iracing_car_id) : null }) : createAdminAssetCarModel({ ...carForm, brand_id: carForm.brand_id ? Number(carForm.brand_id) : null, iracing_car_id: carForm.iracing_car_id ? Number(carForm.iracing_car_id) : null })),
                                    carEditId ? "Car Model aktualisiert." : "Car Model angelegt.",
                                )}
                            >
                                {carEditId ? "Update" : "Anlegen"}
                            </button>
                        </div>
                    </div>
                    {cars.map((row) => (
                        <div key={row.id} style={sectionCardStyle()}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                                <strong>{row.display_name || row.iracing_car_name} {row.brand_name ? `(${row.brand_name})` : ""}</strong>
                                <ImgPreview src={row.image_url} alt={row.display_name || row.iracing_car_name} />
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button className="btn" onClick={() => { setCarEditId(row.id); setCarForm({ brand_id: row.brand_id ? String(row.brand_id) : "", iracing_car_id: row.iracing_car_id ? String(row.iracing_car_id) : "", iracing_car_name: row.iracing_car_name || "", display_name: row.display_name || "", image_url: row.image_url || "", status: row.status || "active" }); }}>In Formular laden</button>
                                <button className="btn" disabled={busy} onClick={() => wrapAction(() => deleteAdminAssetCarModel(row.id), `Car Model geloescht: ${row.display_name || row.iracing_car_name}`)}>Loeschen</button>
                            </div>
                        </div>
                    ))}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>Driver Media</h2>
                    <div style={sectionCardStyle()}>
                        <div style={formGridStyle(180)}>
                            <input className="input" placeholder="driver_id" value={mediaForm.driver_id} onChange={(e) => setMediaForm((p) => ({ ...p, driver_id: e.target.value }))} />
                            <input className="input" placeholder="season_id" value={mediaForm.season_id} onChange={(e) => setMediaForm((p) => ({ ...p, season_id: e.target.value }))} />
                            <input className="input" placeholder="cust_id" value={mediaForm.iracing_cust_id} onChange={(e) => setMediaForm((p) => ({ ...p, iracing_cust_id: e.target.value }))} />
                            <input className="input" placeholder="driver_name_key" value={mediaForm.driver_name_key} onChange={(e) => setMediaForm((p) => ({ ...p, driver_name_key: e.target.value }))} />
                            <input className="input" placeholder="CC" value={mediaForm.country_code} onChange={(e) => setMediaForm((p) => ({ ...p, country_code: e.target.value }))} />
                            <input className="input" placeholder="flag_url" value={mediaForm.flag_url} onChange={(e) => setMediaForm((p) => ({ ...p, flag_url: e.target.value }))} />
                            <input className="input" placeholder="driver_image_url" value={mediaForm.driver_image_url} onChange={(e) => setMediaForm((p) => ({ ...p, driver_image_url: e.target.value }))} />
                            <button
                                className="btn btn--accent"
                                disabled={busy}
                                onClick={() => wrapAction(
                                    () => (mediaEditId ? updateAdminAssetDriverMedia(mediaEditId, { ...mediaForm, driver_id: mediaForm.driver_id ? Number(mediaForm.driver_id) : null, season_id: mediaForm.season_id ? Number(mediaForm.season_id) : null, iracing_cust_id: mediaForm.iracing_cust_id ? Number(mediaForm.iracing_cust_id) : null }) : createAdminAssetDriverMedia({ ...mediaForm, driver_id: mediaForm.driver_id ? Number(mediaForm.driver_id) : null, season_id: mediaForm.season_id ? Number(mediaForm.season_id) : null, iracing_cust_id: mediaForm.iracing_cust_id ? Number(mediaForm.iracing_cust_id) : null })),
                                    mediaEditId ? "Driver Media aktualisiert." : "Driver Media angelegt.",
                                )}
                            >
                                {mediaEditId ? "Update" : "Anlegen"}
                            </button>
                        </div>
                    </div>
                    {media.map((row) => (
                        <div key={row.id} style={sectionCardStyle()}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <strong>{row.driver_display_name || row.driver_iracing_name || row.driver_name_key || `Media #${row.id}`}</strong>
                                <div style={{ display: "flex", gap: 10 }}><ImgPreview src={row.flag_url} alt="flag" w={60} h={34} /><ImgPreview src={row.driver_image_url} alt="driver" w={60} h={60} /></div>
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button className="btn" onClick={() => { setMediaEditId(row.id); setMediaForm({ driver_id: row.driver_id ? String(row.driver_id) : "", season_id: row.season_id ? String(row.season_id) : "", iracing_cust_id: row.iracing_cust_id ? String(row.iracing_cust_id) : "", driver_name_key: row.driver_name_key || "", country_code: row.country_code || "", flag_url: row.flag_url || "", driver_image_url: row.driver_image_url || "", profile_url: row.profile_url || "" }); }}>In Formular laden</button>
                                <button className="btn" disabled={busy} onClick={() => wrapAction(() => deleteAdminAssetDriverMedia(row.id), `Driver Media geloescht: ${row.id}`)}>Loeschen</button>
                            </div>
                        </div>
                    ))}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>Class Assets</h2>
                    <div style={sectionCardStyle()}>
                        <div style={formGridStyle(180)}>
                            <input className="input" placeholder="PRO/INT/AM" value={classForm.class_key} onChange={(e) => setClassForm((p) => ({ ...p, class_key: e.target.value }))} />
                            <input className="input" placeholder="Display Name" value={classForm.display_name} onChange={(e) => setClassForm((p) => ({ ...p, display_name: e.target.value }))} />
                            <input className="input" placeholder="Image URL" value={classForm.image_url} onChange={(e) => setClassForm((p) => ({ ...p, image_url: e.target.value }))} />
                            <input className="input" type="number" min="0" value={classForm.sort_order} onChange={(e) => setClassForm((p) => ({ ...p, sort_order: e.target.value }))} />
                            <button className="btn btn--accent" disabled={busy} onClick={() => wrapAction(() => upsertAdminAssetClass({ ...classForm, sort_order: Number(classForm.sort_order || 0) }), "Class Asset gespeichert.")}>Speichern</button>
                        </div>
                    </div>
                    {classes.map((row) => (
                        <div key={row.class_key} style={sectionCardStyle()}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                                <strong>{row.class_key} - {row.display_name}</strong>
                                <ImgPreview src={row.image_url} alt={row.class_key} />
                            </div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button className="btn" onClick={() => setClassForm({ class_key: row.class_key || "", display_name: row.display_name || "", image_url: row.image_url || "", sort_order: String(row.sort_order || 0) })}>In Formular laden</button>
                                <button className="btn" disabled={busy} onClick={() => wrapAction(() => deleteAdminAssetClass(row.class_key), `Class Asset geloescht: ${row.class_key}`)}>Loeschen</button>
                            </div>
                        </div>
                    ))}
                </section>
            </div>
        </div>
    );
}
